import { useState } from 'react'
import { useAdminFetch, useAdminAction } from '../hooks/useAdminAPI'
import {
  PageHeader, AdminButton, StatusBadge,
  MetricStrip, Metric, Section, Notice, ViewState,
} from '../components'
import { AdminIcon } from '../components/AdminIcon'
import { getClassificationStatus, explainDisagreement } from '../utils/health'
import styles from './Disagreements.module.css'

export function Disagreements() {
  const { data, loading, error, refresh } = useAdminFetch('/admin')

  if (loading && !data) return <ViewState variant="loading" message="Loading classification data…" />
  if (error && !data) return <ViewState variant="error" message={error} onRetry={refresh} />
  if (!data) return null

  const classStats = data.classificationStats
  if (!classStats) return <ViewState variant="empty" message="No classification data available yet." />

  const latestVersion = classStats.latestVersion ?? '1'
  const currentVersionStats = classStats.byVersion?.[latestVersion]
  const currentRate = currentVersionStats?.agreementRate ?? classStats.agreementRate ?? 100
  const overallRate = classStats.agreementRate ?? 100
  const goldenSet = classStats.goldenSet
  const cacheStale = classStats.cacheStalePercent ?? 0
  const samples = classStats.recentSamples ?? []

  // Self-diagnosis: synthesize status into a single message
  const diagnosis = deriveDiagnosis({ goldenSet, currentRate, overallRate, cacheStale, latestVersion, currentVersionStats })

  return (
    <div className={styles.page}>
      <PageHeader
        title="Classification triage"
        subtitle="Review AI vs keyword disagreements and correct mistakes"
        actions={
          <AdminButton
            variant="icon-only"
            icon="refresh"
            label="Refresh data"
            onClick={refresh}
            disabled={loading}
          />
        }
      />

      {/* Pipeline status banner */}
      <Notice variant={diagnosis.variant} title={diagnosis.title}>
        {diagnosis.detail}
      </Notice>

      <MetricStrip>
        <Metric
          label={`v${latestVersion} agreement`}
          value={`${currentRate}%`}
          variant={currentRate < 85 ? 'error' : currentRate < 90 ? 'warning' : 'success'}
        />
        {currentRate !== overallRate && (
          <Metric
            label="Overall (all versions)"
            value={`${overallRate}%`}
            variant={getClassificationStatus(overallRate) === 'error' ? 'error'
              : getClassificationStatus(overallRate) === 'warning' ? 'warning'
              : 'success'}
          />
        )}
        <Metric label="Total classified" value={classStats.total ?? 0} />
        {goldenSet && (
          <Metric
            label="Golden set"
            value={`${goldenSet.passed}/${goldenSet.total}`}
            variant={goldenSet.failed > 0 ? 'error' : 'success'}
          />
        )}
      </MetricStrip>

      {/* How it works */}
      <Notice variant="info" title="How classification works">
        Every product goes through the AI classification pipeline (cache → semantic → LLM → keyword fallback). A "disagreement" means the AI and keyword fallback gave different answers. Most are harmless — the AI often correctly rejects non-produce items. Override only when the AI is genuinely wrong.
      </Notice>

      {/* Resolution method breakdown */}
      {classStats.byTier && Object.keys(classStats.byTier).length > 0 && (
        <Section title="Resolution methods">
          <div className={styles.tierGrid}>
            {Object.entries(classStats.byTier).map(([tier, count]) => (
              <div key={tier} className={styles.tierCard}>
                <span className={styles.tierLabel}>{TIER_LABELS[tier] || tier}</span>
                <span className={styles.tierValue}>{count}</span>
                <span className={styles.tierPercent}>
                  {classStats.total > 0 ? Math.round(count / classStats.total * 100) : 0}%
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Disagreement triage */}
      <Section
        title="Recent disagreements"
        action={
          samples.length > 0 && (
            <span className={styles.triageHint}>
              Reviewing trains the system
            </span>
          )
        }
      >
        {samples.length === 0 ? (
          <div className={styles.emptyState}>
            <AdminIcon name="check" size={24} className={styles.emptyIcon} />
            <p className={styles.emptyTitle}>All caught up</p>
            <p className={styles.emptyDetail}>No disagreements need your review right now.</p>
          </div>
        ) : (
          <div className={styles.triageList}>
            {samples.map((sample, i) => (
              <DisagreementCard
                key={i}
                sample={sample}
                onAction={refresh}
              />
            ))}
          </div>
        )}
      </Section>

      {/* Disagreement type breakdown */}
      {classStats.disagreeTypes && Object.keys(classStats.disagreeTypes).length > 0 && (
        <Section title="Disagreement breakdown">
          <div className={styles.breakdownGroup}>
            {Object.entries(classStats.disagreeTypes).map(([type, count]) => (
              <div key={type} className={styles.breakdownRow}>
                <span className={styles.breakdownLabel}>{DISAGREE_LABELS[type] || type}</span>
                <span className={styles.breakdownValue}>{count}</span>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  )
}

function DisagreementCard({ sample, onAction }) {
  const [resolved, setResolved] = useState(null) // 'dismissed' | 'overridden'
  const explanation = explainDisagreement(sample)

  const overrideAction = useAdminAction('/admin/actions', {
    onSuccess: () => { setResolved('overridden'); onAction?.() },
  })
  const dismissAction = useAdminAction('/admin/actions', {
    onSuccess: () => setResolved('dismissed'),
  })

  const cacheResult = formatResult(sample.is_bio, sample.category)
  const fallbackResult = formatResult(sample.fallback_is_bio, sample.fallback_category)
  const isLoading = overrideAction.loading || dismissAction.loading

  if (resolved) {
    return (
      <div className={`${styles.card} ${styles.cardResolved}`}>
        <div className={styles.cardResolvedContent}>
          <AdminIcon
            name="check"
            size={14}
            className={resolved === 'dismissed' ? styles.successIcon : styles.warningIcon}
          />
          <span className={styles.cardProductName}>{sample.product_name}</span>
          <span className={styles.resolvedLabel}>
            {resolved === 'dismissed' ? 'AI confirmed correct' : 'Overridden to fallback'}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <span className={styles.cardProductName}>{sample.product_name}</span>
      </div>

      <div className={styles.cardComparison}>
        <div className={styles.comparisonSide}>
          <span className={styles.comparisonLabel}>
            <span className={`${styles.tierBadge} ${styles.tierBadgeAi}`}>{sample.tier}</span>
            says
          </span>
          <span className={styles.comparisonResult}>{cacheResult}</span>
        </div>
        <span className={styles.comparisonVs}>vs</span>
        <div className={styles.comparisonSide}>
          <span className={styles.comparisonLabel}>
            <span className={`${styles.tierBadge} ${styles.tierBadgeFallback}`}>keywords</span>
            say
          </span>
          <span className={styles.comparisonResult}>{fallbackResult}</span>
        </div>
      </div>

      <p className={styles.cardExplanation}>{explanation.explanation}</p>

      <div className={styles.cardActions}>
        <button
          className={styles.btnDismiss}
          onClick={() => dismissAction.execute({
            action: 'dismiss-disagreement',
            productName: sample.product_name,
          })}
          disabled={isLoading}
          type="button"
        >
          <AdminIcon name="check" size={14} />
          AI is correct
        </button>
        {sample.fallback_category && (
          <button
            className={styles.btnOverride}
            onClick={() => overrideAction.execute({
              action: 'override-classification',
              productName: sample.product_name,
              isBio: sample.fallback_is_bio,
              category: sample.fallback_category,
            })}
            disabled={isLoading}
            type="button"
          >
            Override to {fallbackResult}
          </button>
        )}
      </div>
    </div>
  )
}

function formatResult(isBio, category) {
  if (!isBio) return 'not bio'
  return category ? `bio → ${category}` : 'bio (no category)'
}

const TIER_LABELS = {
  cache: 'Cache hit',
  llm: 'LLM classified',
  semantic: 'Semantic match',
  override: 'Manual override',
  fallback: 'Keyword fallback',
}

const DISAGREE_LABELS = {
  bio_status: 'Bio status differs',
  category: 'Category differs',
  category_found: 'AI found category, keywords didn\'t',
  category_missed: 'Keywords found category, AI didn\'t',
  other: 'Other',
}

/**
 * Derive a one-sentence diagnosis from pipeline health signals.
 * Used for the status banner at the top of the triage view.
 */
function deriveDiagnosis({ goldenSet, currentRate, overallRate, cacheStale, latestVersion, currentVersionStats }) {
  // Golden set failures — something is genuinely broken
  if (goldenSet && goldenSet.failed > 0) {
    const names = goldenSet.failures?.map(f => f.name).join(', ') || 'unknown'
    return {
      variant: 'error',
      title: `Pipeline broken — ${goldenSet.failed} golden set failure(s)`,
      detail: `Known-good products were misclassified: ${names}. The LLM or prompt needs investigation.`,
    }
  }

  // Golden set passes and everything is healthy
  if (currentRate >= 90 && overallRate >= 90) {
    return {
      variant: 'success',
      title: 'Pipeline healthy',
      detail: `Classifier v${latestVersion}: ${currentRate}% agreement on ${currentVersionStats?.total ?? 0} products.${goldenSet ? ` Golden set: ${goldenSet.passed}/${goldenSet.total} passing.` : ''}`,
    }
  }

  // Current version is fine but overall is dragged down by stale cache
  if (currentRate >= 85 && overallRate < 90 && cacheStale > 10) {
    return {
      variant: 'info',
      title: `Pipeline healthy — cache warming up after v${latestVersion} upgrade`,
      detail: `Current version: ${currentRate}% agreement. Overall (${overallRate}%) is lower because ${cacheStale}% of entries are from older versions. These will be replaced over the next few scrape runs.`,
    }
  }

  // Current version underperforming
  if (currentRate < 85) {
    return {
      variant: 'warning',
      title: `Current classifier (v${latestVersion}) at ${currentRate}% agreement`,
      detail: `${currentVersionStats?.disagreements ?? 0} disagreements on ${currentVersionStats?.total ?? 0} products. Review recent disagreements to find systematic errors.`,
    }
  }

  // Moderate — overall rate between 85-90%, no cache warmup explanation
  return {
    variant: 'warning',
    title: `${overallRate}% agreement rate — review recommended`,
    detail: `Target is 90%+.${goldenSet ? ` Golden set: ${goldenSet.passed}/${goldenSet.total} passing.` : ''} Override incorrect classifications to train the system.`,
  }
}
